import { GelatoRelay, TaskState, TransactionStatusResponse } from "@gelatonetwork/relay-sdk";

const gelatoRelayClient = new GelatoRelay();

// The SDK's task-status subscriptions use a WebSocket endpoint that is not available in all environments.
// Keep the same local subscription API while obtaining status updates through the supported HTTP endpoint.
const GELATO_STATUS_POLL_INTERVAL = 500;
const finalTaskStates = new Set([TaskState.ExecSuccess, TaskState.ExecReverted, TaskState.Cancelled]);

type TaskStatusHandler = (taskStatus: TransactionStatusResponse) => void;

const taskStatusHandlers = new Set<TaskStatusHandler>();
const taskStatusErrorHandlers = new Set<(error: Error) => void>();
const taskStatusPollers = new Map<string, ReturnType<typeof setTimeout> | undefined>();

async function pollTaskStatus(taskId: string) {
  if (!taskStatusPollers.has(taskId)) {
    return;
  }

  try {
    const taskStatus = await gelatoRelayClient.getTaskStatus(taskId);

    if (!taskStatusPollers.has(taskId)) {
      return;
    }

    if (taskStatus) {
      taskStatusHandlers.forEach((handler) => handler(taskStatus));

      if (finalTaskStates.has(taskStatus.taskState)) {
        taskStatusPollers.delete(taskId);
        return;
      }
    }
  } catch (error) {
    const pollingError = error instanceof Error ? error : new Error(String(error));
    taskStatusErrorHandlers.forEach((handler) => handler(pollingError));
  }

  if (taskStatusPollers.has(taskId)) {
    taskStatusPollers.set(
      taskId,
      setTimeout(() => pollTaskStatus(taskId), GELATO_STATUS_POLL_INTERVAL)
    );
  }
}

export const gelatoRelay = {
  getEstimatedFee: gelatoRelayClient.getEstimatedFee,
  getTaskStatus: gelatoRelayClient.getTaskStatus,
  onTaskStatusUpdate(handler: TaskStatusHandler) {
    taskStatusHandlers.add(handler);
  },
  offTaskStatusUpdate(handler: TaskStatusHandler) {
    taskStatusHandlers.delete(handler);
  },
  onError(handler: (error: Error) => void) {
    taskStatusErrorHandlers.add(handler);
  },
  offError(handler: (error: Error) => void) {
    taskStatusErrorHandlers.delete(handler);
  },
  async subscribeTaskStatusUpdate(taskId: string) {
    if (taskStatusPollers.has(taskId)) {
      return;
    }

    taskStatusPollers.set(taskId, undefined);
    await pollTaskStatus(taskId);
  },
  async unsubscribeTaskStatusUpdate(taskId: string) {
    const timeout = taskStatusPollers.get(taskId);

    if (timeout !== undefined) {
      clearTimeout(timeout);
    }

    taskStatusPollers.delete(taskId);
  },
};
